import type { MediaItem } from '../types';

export type Pace = 'relaxed' | 'cinematic' | 'dynamic';
export type TransitionStyle = 'auto' | 'dissolve' | 'fade-black' | 'push' | 'zoom' | 'blur' | 'cut';
export type TransitionKind = Exclude<TransitionStyle, 'auto'>;
export type MotionLevel = 'off' | 'subtle' | 'standard' | 'expressive';
/** 'ask' = ask once on the opening card. */
export type PhotoAnimation = 'ask' | 'still' | 'motion' | 'depth';
export type ColorTreatment = 'natural' | 'warm' | 'film';

/** Mirrors server/src/services/player-preferences.service.ts. */
export interface PlayerPreferences {
  pace: Pace;
  transition: TransitionStyle;
  photoAnimation: PhotoAnimation;
  motion: MotionLevel;
  colorTreatment: ColorTreatment;
  vignette: boolean;
  openingCard: boolean;
  endingCard: boolean;
  overlays: 'off' | 'minimal';
  /** AI story lines under each memory, when the episode has them. */
  storyLines: 'on' | 'off';
  /** Subtitle language: 'story' = the story's own language. */
  subtitles: 'off' | 'story' | 'en' | 'ur';
  /** Narration audio language: 'story' = the story's own language. */
  narration: 'off' | 'story' | 'en' | 'ur';
  /** English narrator (ElevenLabs voice id); null = server default (George). */
  narrationVoice: string | null;
  /** Urdu narrator id; null = default Urdu voice. */
  narrationVoiceUr: string | null;
  /** 'system' follows the device; 'on' / 'off' override it. */
  reducedMotion: 'system' | 'on' | 'off';
  musicEnabled: boolean;
  musicVolume: number;
  videoVolume: number;
  playlist: string[];
}

export const DEFAULT_PREFERENCES: PlayerPreferences = {
  pace: 'cinematic',
  transition: 'auto',
  photoAnimation: 'ask',
  motion: 'standard',
  colorTreatment: 'natural',
  vignette: true,
  openingCard: true,
  endingCard: true,
  overlays: 'minimal',
  storyLines: 'on',
  subtitles: 'story',
  narration: 'story',
  narrationVoice: null,
  narrationVoiceUr: null,
  reducedMotion: 'system',
  musicEnabled: true,
  musicVolume: 0.6,
  videoVolume: 1,
  playlist: [],
};

export type SoundtrackCategory = 'EMOTIONAL' | 'NOSTALGIC' | 'PEACEFUL' | 'ADVENTURE' | 'CELEBRATION' | 'UNCATEGORIZED';

export const CATEGORY_LABELS: Record<SoundtrackCategory, string> = {
  EMOTIONAL: 'Emotional',
  NOSTALGIC: 'Nostalgic',
  PEACEFUL: 'Peaceful',
  ADVENTURE: 'Adventure',
  CELEBRATION: 'Celebration',
  UNCATEGORIZED: 'Other',
};

export interface SoundtrackTrack {
  id: string;
  title: string;
  artist: string | null;
  category: SoundtrackCategory;
  durationSeconds: number | null;
  mimeType: string;
  fileSize: number;
  originalFilename: string;
  url: string;
  /** One of the public-domain tracks that come with Memoryflix (can't be renamed or removed). */
  builtIn: boolean;
}

export interface SoundtrackLibrary {
  tracks: SoundtrackTrack[];
  limits: { maxFileBytes: number; extensions: string[] };
}

/** A keyframe pose for Ken Burns: scale plus translation in % of the element. */
export interface Pose {
  scale: number;
  x: number;
  y: number;
}

export interface MotionPlan {
  name: string;
  from: Pose;
  to: Pose;
  durationMs: number;
}

/**
 * Camera pose for the 3D photo effect. x / y shift near things sideways (as a
 * fraction of the image); dolly enlarges near things. Far things barely move,
 * which is what reads as depth.
 */
export interface ParallaxPose {
  x: number;
  y: number;
  dolly: number;
}

/** 3D depth move, rendered with WebGL from the photo's depth map. */
export interface ParallaxPlan {
  from: ParallaxPose;
  to: ParallaxPose;
  durationMs: number;
}

export interface TransitionSpec {
  kind: TransitionKind;
  ms: number;
}

/** One memory as it will be presented in the film. Pure data, deterministic. */
export interface Shot {
  index: number;
  item: MediaItem;
  kind: 'photo' | 'video';
  /**
   * Time from the start of this shot's entrance until the next shot starts
   * entering. Photos: from the pace. Videos: the clip's own length (the
   * element's 'ended' event is what actually advances). Either is stretched
   * to fit the memory's narration; a video then holds its last frame.
   */
  holdMs: number;
  /** Narration starts this long after the shot starts entering (0 = no narration). */
  voiceDelayMs: number;
  /** Length of this shot's narration in the language being played (0 = none). */
  voiceMs: number;
  /** How this shot enters. */
  transitionIn: TransitionSpec;
  /** Ken Burns for photos; null for video and when motion is off. */
  motion: MotionPlan | null;
  /** 3D depth move for photos when the user chose it and a depth map exists. */
  parallax: ParallaxPlan | null;
  /** First shot of a new scene (large time gap or a different place). */
  sceneStart: boolean;
  /** Show "taken on" date only when the file itself recorded it. */
  trustedDate: boolean;
}
